import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { PursuitPPComparison, PursuitPPContent, PursuitPPMetric } from '../../../../../../gen/ts/mono/datagateway/v1/pursuit_pp_pb';
import { pursuitPPClient } from './api';
import { ContentReadOnly } from './ContentReadOnly';
import { Choice, Facts, lines, options, TextField } from './Fields';
export function TailoringEditor({ selection, onSaved, onReload, context }) {
    const [draft, setDraft] = useState(()=>selection.content?.clone() ?? new PursuitPPContent());
    const [expectedRevision, setExpectedRevision] = useState(selection.revision);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [conflict, setConflict] = useState(false);
    const [latest, setLatest] = useState();
    const [history, setHistory] = useState();
    const [status, setStatus] = useState('');
    const update = (key, value)=>{
        setDraft(new PursuitPPContent({
            ...draft,
            [key]: value
        }));
        setStatus('Unsaved changes');
    };
    async function readRevision(revision) {
        setBusy(true);
        setError('');
        try {
            const result = await pursuitPPClient.getPursuitPP({
                captureProjectId: selection.captureProjectId,
                id: selection.id,
                revision
            });
            if (!result.selection || result.selection.sourceWithdrawn) {
                await onReload();
                return;
            }
            if (revision === 0) setLatest(result.selection);
            else setHistory(result.selection);
        } catch  {
            setHistory(undefined);
            setLatest(undefined);
            setError('This revision is unavailable or access was removed. Retry loading current access.');
            await onReload();
        } finally{
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(Facts, {
                title: "Lineage and permitted use",
                children: /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.pursuitPP.lineage,
                    children: [
                        "Source Library ID: ",
                        selection.libraryItemId,
                        /*#__PURE__*/ _jsx("br", {}),
                        "Source version: ",
                        selection.sourceVersion,
                        /*#__PURE__*/ _jsx("br", {}),
                        "Local revision: ",
                        selection.revision,
                        /*#__PURE__*/ _jsx("br", {}),
                        "Material: ",
                        selection.materialKind,
                        " (citations do not independently validate claims)",
                        /*#__PURE__*/ _jsx("br", {}),
                        "Current status: ",
                        selection.reuseStatus,
                        /*#__PURE__*/ _jsx("br", {}),
                        "Audience: ",
                        selection.audience || 'Owner draft',
                        /*#__PURE__*/ _jsx("br", {}),
                        "Restrictions:",
                        ' ',
                        selection.sourceRestrictions || 'No additional restrictions recorded; external use is not approved',
                        /*#__PURE__*/ _jsx("br", {}),
                        "Source version:",
                        ' ',
                        selection.releaseId || 'Manual draft; the source date is for reference, not a saved copy of the source',
                        /*#__PURE__*/ _jsx("br", {}),
                        "Reuse: ",
                        selection.reuseId || 'Manual selection',
                        /*#__PURE__*/ _jsx("br", {}),
                        "Local tailoring: externally unapproved"
                    ]
                })
            }),
            selection.approvedSourceContent && /*#__PURE__*/ _jsx(Facts, {
                title: "Approved source derivative — separate from local tailoring",
                children: selection.approvedSourceContent
            }),
            /*#__PURE__*/ _jsxs("details", {
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        children: "Read earlier local revision"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-2",
                        children: Array.from({
                            length: selection.revision
                        }, (_, index)=>index + 1).map((revision)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                type: "button",
                                variant: "secondary",
                                "data-testid": Testids.pursuitPP.history,
                                disabled: busy,
                                onClick: ()=>void readRevision(revision),
                                children: [
                                    "Revision ",
                                    revision
                                ]
                            }, revision))
                    })
                ]
            }),
            history && /*#__PURE__*/ _jsx(Facts, {
                title: `Read only — revision ${history.revision}`,
                children: /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.pursuitPP.historyEvidence,
                    children: /*#__PURE__*/ _jsx(ContentReadOnly, {
                        content: history.content
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("form", {
                className: "space-y-3",
                onSubmit: async (event)=>{
                    event.preventDefault();
                    if (draft.metrics.some((m)=>!m.measuredThing.trim()) || draft.comparisons.some((c)=>c.assessment === 'missing_evidence' ? !c.missingEvidence.trim() : !c.source.trim() || !c.pursuitContext.trim() || !c.referenceContext.trim())) {
                        setError('Each metric needs a measured thing. Matches and differences need both contexts and a source; missing-evidence assessments need the missing detail.');
                        return;
                    }
                    setBusy(true);
                    setError('');
                    setStatus('');
                    try {
                        const result = await pursuitPPClient.revisePursuitPP({
                            captureProjectId: selection.captureProjectId,
                            id: selection.id,
                            expectedRevision,
                            content: draft
                        });
                        if (!result.selection) throw new Error('No saved revision');
                        onSaved(result.selection);
                        setExpectedRevision(result.selection.revision);
                        setConflict(false);
                        setLatest(undefined);
                        setStatus('Saved local revision');
                    } catch (err) {
                        const aborted = err instanceof ConnectError && err.code === Code.Aborted;
                        setConflict(aborted);
                        setError(aborted ? 'Another author saved a revision. Your draft is retained. Read the latest revision and reconcile before saving again.' : 'Save failed. Your draft is retained. Check current access and retry.');
                        if (err instanceof ConnectError && [
                            Code.PermissionDenied,
                            Code.NotFound
                        ].includes(err.code)) await onReload();
                    } finally{
                        setBusy(false);
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold",
                        children: "Pursuit tailoring — manual, unapproved"
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Local relevance narrative",
                        multiline: true,
                        value: draft.narrative,
                        testId: Testids.pursuitPP.narrative,
                        onChange: (v)=>update('narrative', v)
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Outcomes",
                        multiline: true,
                        value: draft.outcomes,
                        onChange: (v)=>update('outcomes', v)
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Scope examples (one per line)",
                        multiline: true,
                        value: draft.scopeExamples.join('\n'),
                        onChange: (v)=>setDraft(new PursuitPPContent({
                                ...draft,
                                scopeExamples: lines(v)
                            }))
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Limitations",
                        multiline: true,
                        value: draft.limitations,
                        onChange: (v)=>update('limitations', v)
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Citations: calls, emails, upload references, old proposals (one per line)",
                        multiline: true,
                        value: draft.sources.join('\n'),
                        onChange: (v)=>setDraft(new PursuitPPContent({
                                ...draft,
                                sources: lines(v)
                            }))
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Manual fit assessment (not an eligibility verdict)",
                        multiline: true,
                        value: draft.fitAssessment,
                        onChange: (v)=>update('fitAssessment', v)
                    }),
                    /*#__PURE__*/ _jsx("h4", {
                        className: "font-medium",
                        children: "Structured metrics — record evidence; leave unknown values blank"
                    }),
                    draft.metrics.map((metric, index)=>/*#__PURE__*/ _jsxs("fieldset", {
                            className: "grid gap-2 rounded-md border p-3 sm:grid-cols-2",
                            children: [
                                /*#__PURE__*/ _jsxs("legend", {
                                    children: [
                                        "Metric ",
                                        index + 1
                                    ]
                                }),
                                [
                                    'measuredThing',
                                    'value',
                                    'unit',
                                    'period',
                                    'baseline',
                                    'source'
                                ].map((key)=>/*#__PURE__*/ _jsx(TextField, {
                                        label: key === 'measuredThing' ? 'Measured thing' : key,
                                        value: metric[key],
                                        testId: key === 'measuredThing' ? Testids.pursuitPP.metricThing : key === 'value' ? Testids.pursuitPP.metricValue : key === 'unit' ? Testids.pursuitPP.metricUnit : key === 'period' ? Testids.pursuitPP.metricPeriod : key === 'source' ? Testids.pursuitPP.metricSource : undefined,
                                        onChange: (value)=>setDraft(new PursuitPPContent({
                                                ...draft,
                                                metrics: draft.metrics.map((m, i)=>i === index ? new PursuitPPMetric({
                                                        ...m,
                                                        [key]: value
                                                    }) : m)
                                            }))
                                    }, key)),
                                /*#__PURE__*/ _jsx(Choice, {
                                    label: "Metric reuse status",
                                    value: metric.reuseStatus,
                                    options: options([
                                        'unknown',
                                        'pending',
                                        'restricted'
                                    ]),
                                    onChange: (reuseStatus)=>setDraft(new PursuitPPContent({
                                            ...draft,
                                            metrics: draft.metrics.map((m, i)=>i === index ? new PursuitPPMetric({
                                                    ...m,
                                                    reuseStatus
                                                }) : m)
                                        }))
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    type: "button",
                                    variant: "secondary",
                                    onClick: ()=>setDraft(new PursuitPPContent({
                                            ...draft,
                                            metrics: draft.metrics.filter((_, i)=>i !== index)
                                        })),
                                    children: [
                                        "Remove metric ",
                                        index + 1
                                    ]
                                })
                            ]
                        }, index)),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        "data-testid": Testids.pursuitPP.addMetric,
                        onClick: ()=>setDraft(new PursuitPPContent({
                                ...draft,
                                metrics: [
                                    ...draft.metrics,
                                    new PursuitPPMetric({
                                        reuseStatus: 'unknown'
                                    })
                                ]
                            })),
                        children: "Add metric"
                    }),
                    /*#__PURE__*/ _jsx("h4", {
                        className: "font-medium",
                        children: "Matches, differences and missing evidence — manual comparison"
                    }),
                    draft.comparisons.map((comparison, index)=>/*#__PURE__*/ _jsxs("fieldset", {
                            "data-testid": Testids.pursuitPP.comparison,
                            className: "grid gap-2 rounded-md border p-3 sm:grid-cols-2",
                            children: [
                                /*#__PURE__*/ _jsxs("legend", {
                                    children: [
                                        "Comparison ",
                                        index + 1
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Choice, {
                                    label: "Dimension",
                                    value: comparison.dimension,
                                    options: options([
                                        'scope',
                                        'customer',
                                        'scale',
                                        'capabilities',
                                        'needs'
                                    ]),
                                    onChange: (dimension)=>setDraft(new PursuitPPContent({
                                            ...draft,
                                            comparisons: draft.comparisons.map((c, i)=>i === index ? new PursuitPPComparison({
                                                    ...c,
                                                    dimension
                                                }) : c)
                                        }))
                                }),
                                /*#__PURE__*/ _jsx(Choice, {
                                    label: "Assessment",
                                    value: comparison.assessment,
                                    options: options([
                                        'match',
                                        'difference',
                                        'missing_evidence'
                                    ]),
                                    onChange: (assessment)=>setDraft(new PursuitPPContent({
                                            ...draft,
                                            comparisons: draft.comparisons.map((c, i)=>i === index ? new PursuitPPComparison({
                                                    ...c,
                                                    assessment
                                                }) : c)
                                        }))
                                }),
                                [
                                    'pursuitContext',
                                    'referenceContext',
                                    'source',
                                    'missingEvidence'
                                ].map((key)=>/*#__PURE__*/ _jsx(TextField, {
                                        label: {
                                            pursuitContext: 'Pursuit context',
                                            referenceContext: 'Reference context',
                                            source: 'Comparison source',
                                            missingEvidence: 'Missing comparison evidence'
                                        }[key],
                                        value: comparison[key],
                                        onChange: (value)=>setDraft(new PursuitPPContent({
                                                ...draft,
                                                comparisons: draft.comparisons.map((c, i)=>i === index ? new PursuitPPComparison({
                                                        ...c,
                                                        [key]: value
                                                    }) : c)
                                            }))
                                    }, key)),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    type: "button",
                                    variant: "secondary",
                                    onClick: ()=>setDraft(new PursuitPPContent({
                                            ...draft,
                                            comparisons: draft.comparisons.filter((_, i)=>i !== index)
                                        })),
                                    children: [
                                        "Remove comparison ",
                                        index + 1
                                    ]
                                })
                            ]
                        }, index)),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        "data-testid": Testids.pursuitPP.addComparison,
                        onClick: ()=>setDraft(new PursuitPPContent({
                                ...draft,
                                comparisons: [
                                    ...draft.comparisons,
                                    new PursuitPPComparison({
                                        dimension: 'scope',
                                        assessment: 'missing_evidence',
                                        pursuitContext: context.scope,
                                        missingEvidence: 'Confirm comparable scope and permission to reuse with the provider.'
                                    })
                                ]
                            })),
                        children: "Add manual comparison"
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Missing evidence (one per line)",
                        multiline: true,
                        testId: Testids.pursuitPP.missing,
                        value: draft.missingEvidence.join('\n'),
                        onChange: (v)=>setDraft(new PursuitPPContent({
                                ...draft,
                                missingEvidence: lines(v)
                            }))
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Targeted follow-up (record only; nothing is sent)",
                        multiline: true,
                        testId: Testids.pursuitPP.followUp,
                        value: draft.followUp,
                        onChange: (v)=>update('followUp', v)
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        "data-testid": Testids.pursuitPP.error,
                        children: error
                    }),
                    conflict && /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        disabled: busy,
                        onClick: ()=>void readRevision(0),
                        children: "Read latest for reconciliation"
                    }),
                    latest && /*#__PURE__*/ _jsxs(Facts, {
                        title: `Latest saved revision ${latest.revision} — compare with your retained draft`,
                        children: [
                            /*#__PURE__*/ _jsx(ContentReadOnly, {
                                content: latest.content
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                type: "button",
                                variant: "secondary",
                                onClick: ()=>{
                                    setExpectedRevision(latest.revision);
                                    setConflict(false);
                                    setError('');
                                },
                                children: [
                                    "I reconciled my draft with revision ",
                                    latest.revision
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.pursuitPP.saved,
                        children: busy ? 'Loading / saving revision…' : status
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.pursuitPP.save,
                        disabled: busy || conflict,
                        children: "Save local revision"
                    })
                ]
            })
        ]
    });
}
